/**
 * PMPM Billing Admin Page
 * Manage rate cards, member eligibility (individual + bulk), billing generation,
 * and invoice PDF download. Integrated with programs.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Users, Calendar, CheckCircle, Loader2, Trash2, Calculator,
  Search, FileText, Download, UserPlus, X, Check, Building, ChevronDown
} from 'lucide-react';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiClient } from '@/services/apiClient';

// ── Types ──────────────────────────────────────────────────────────────

interface Program { id: number; name: string; program_type: string | null; is_active: boolean; }

interface RateCard {
  id: number; rate_name: string; rate_per_member_month: number; mco_id: number | null;
  program_id: number | null; program_name: string | null; effective_date: string;
  end_date: string | null; risk_adjustment_factor: number; is_active: boolean; created_at: string;
}

interface Eligibility {
  id: number; member_id: number; member_name: string; mco_id: number | null;
  program_id: number | null; program_name: string | null;
  rate_card_id: number | null; rate_card_name: string | null;
  eligible_start: string; eligible_end: string | null; status: string; created_at: string;
}

interface BillingPeriod {
  id: number; period_month: number; period_year: number; eligible_member_count: number;
  rate_per_member: number; total_amount: number; status: string;
  mco_id: number | null; program_name: string | null; rate_card_name: string | null;
  rate_card_id: number | null; generated_at: string;
}

interface MemberSearchResult {
  id: number; first_name: string | null; last_name: string | null;
  member_number: string | null; already_eligible: boolean;
}

// ── Component ──────────────────────────────────────────────────────────

export default function PMPMBillingPage() {
  const [activeTab, setActiveTab] = useState<'rate-cards' | 'eligibility' | 'billing'>('rate-cards');
  const [rateCards, setRateCards] = useState<RateCard[]>([]);
  const [eligibility, setEligibility] = useState<Eligibility[]>([]);
  const [billingPeriods, setBillingPeriods] = useState<BillingPeriod[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Rate card form ──
  const [showRateForm, setShowRateForm] = useState(false);
  const [rcName, setRcName] = useState('');
  const [rcRate, setRcRate] = useState('');
  const [rcEffDate, setRcEffDate] = useState('');
  const [rcMco, setRcMco] = useState('');
  const [rcRaf, setRcRaf] = useState('1.000');
  const [rcProgramId, setRcProgramId] = useState('');
  const [rcNotes, setRcNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Eligibility: single member ──
  const [showEligForm, setShowEligForm] = useState(false);
  const [eligRateCardId, setEligRateCardId] = useState('');
  const [eligProgramId, setEligProgramId] = useState('');
  const [eligStart, setEligStart] = useState(new Date().toISOString().slice(0, 10));

  // ── Eligibility: member search ──
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState<MemberSearchResult[]>([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<MemberSearchResult[]>([]);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Billing generation ──
  const [genMonth, setGenMonth] = useState(String(new Date().getMonth() + 1));
  const [genYear, setGenYear] = useState(String(new Date().getFullYear()));
  const [genRateCardId, setGenRateCardId] = useState('');
  const [generating, setGenerating] = useState(false);

  // ── PDF download tracking ──
  const [downloadingPdf, setDownloadingPdf] = useState<number | null>(null);

  // ── Data fetching ──
  const fetchData = async () => {
    setLoading(true);
    try {
      const [rc, el, bp, prg] = await Promise.all([
        apiClient.get('/api/pmpm/rate-cards').then(r => r.data).catch(() => []),
        apiClient.get('/api/pmpm/eligibility').then(r => r.data).catch(() => []),
        apiClient.get('/api/pmpm/billing-periods').then(r => r.data).catch(() => []),
        apiClient.get('/api/pmpm/programs').then(r => r.data).catch(() => []),
      ]);
      setRateCards(rc); setEligibility(el); setBillingPeriods(bp); setPrograms(prg);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Member search with debounce ──
  const searchMembers = useCallback(async (query: string) => {
    if (query.length < 2) { setMemberResults([]); return; }
    setSearchingMembers(true);
    try {
      // Use the proven unified member search endpoint (returns { members: [...], family_members: [...] })
      const resp = await apiClient.get(`/api/members/search/unified?q=${encodeURIComponent(query)}&limit=20`);
      const members = resp.data?.members || [];

      // Check which are already PMPM-eligible
      let eligibleIds = new Set<number>();
      try {
        const eligResp = await apiClient.get('/api/pmpm/eligibility?status=active');
        eligibleIds = new Set((eligResp.data || []).map((e: any) => e.member_id));
      } catch { /* ignore */ }

      setMemberResults(members.map((m: any) => ({
        id: m.id,
        first_name: m.first_name,
        last_name: m.last_name,
        member_number: m.member_number,
        already_eligible: eligibleIds.has(m.id),
      })));
    } catch (err) {
      console.error('Member search failed:', err);
      setMemberResults([]);
    }
    finally { setSearchingMembers(false); }
  }, [eligRateCardId]);

  const handleMemberQueryChange = (val: string) => {
    setMemberQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchMembers(val), 300);
  };

  const toggleMemberSelection = (m: MemberSearchResult) => {
    setSelectedMembers(prev =>
      prev.find(s => s.id === m.id) ? prev.filter(s => s.id !== m.id) : [...prev, m]
    );
  };

  const selectAllVisible = () => {
    const eligible = memberResults.filter(m => !m.already_eligible);
    setSelectedMembers(prev => {
      const existingIds = new Set(prev.map(s => s.id));
      const newOnes = eligible.filter(m => !existingIds.has(m.id));
      return [...prev, ...newOnes];
    });
  };

  // ── Create rate card ──
  const createRateCard = async () => {
    setSaving(true);
    try {
      await apiClient.post('/api/pmpm/rate-cards', {
        rate_name: rcName, rate_per_member_month: parseFloat(rcRate),
        effective_date: rcEffDate, mco_id: rcMco ? parseInt(rcMco) : null,
        risk_adjustment_factor: parseFloat(rcRaf),
        program_id: rcProgramId ? parseInt(rcProgramId) : null,
        notes: rcNotes || null,
      });
      setShowRateForm(false); setRcName(''); setRcRate(''); setRcEffDate(''); setRcNotes('');
      setRcProgramId(''); setRcMco('');
      await fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // ── Assign members (single or bulk) ──
  const assignMembers = async () => {
    if (selectedMembers.length === 0) return;
    setSaving(true);
    try {
      const body = {
        member_ids: selectedMembers.map(m => m.id),
        eligible_start: eligStart,
        rate_card_id: eligRateCardId && eligRateCardId !== 'none' ? parseInt(eligRateCardId) : null,
        program_id: eligProgramId && eligProgramId !== 'none' ? parseInt(eligProgramId) : null,
      };
      const resp = await apiClient.post('/api/pmpm/eligibility/bulk', body);
      const { added, skipped } = resp.data;
      if (skipped > 0) alert(`Added ${added} members. ${skipped} skipped (already eligible).`);
      setSelectedMembers([]); setMemberQuery(''); setMemberResults([]);
      setShowEligForm(false);
      await fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // ── Remove eligibility ──
  const removeEligibility = async (id: number) => {
    if (!window.confirm('Close this PMPM eligibility?')) return;
    await apiClient.delete(`/api/pmpm/eligibility/${id}`);
    await fetchData();
  };

  // ── Generate billing ──
  const generateBilling = async () => {
    if (!genRateCardId) { alert('Select a rate card'); return; }
    setGenerating(true);
    try {
      const resp = await apiClient.post(`/api/pmpm/generate-billing?month=${genMonth}&year=${genYear}&rate_card_id=${genRateCardId}`);
      alert(`Billing generated: ${resp.data.eligible_member_count} members × $${resp.data.rate_per_member} = $${Number(resp.data.total_amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`);
      await fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  // ── Approve billing period ──
  const approvePeriod = async (id: number) => {
    try {
      await apiClient.post(`/api/pmpm/billing-periods/${id}/approve`);
      await fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to approve'); }
  };

  // ── Delete billing period ──
  const deletePeriod = async (id: number) => {
    if (!confirm('Delete this billing period? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/api/pmpm/billing-periods/${id}`);
      await fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  // ── Download invoice PDF ──
  const downloadInvoicePdf = async (id: number) => {
    setDownloadingPdf(id);
    try {
      // Build authenticated URL and fetch as blob directly
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const resp = await fetch(`/api/pmpm/billing-periods/${id}/invoice-pdf`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(cboId ? { 'X-CBO-ID': cboId } : {}),
        },
      });
      if (!resp.ok) {
        const errText = await resp.text().catch(() => 'Unknown error');
        throw new Error(errText);
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `PMPM_Invoice_${id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error('Invoice PDF download failed:', e);
      alert('Failed to download invoice PDF. ' + (e.message || ''));
    }
    finally { setDownloadingPdf(null); }
  };

  // ── Open PDF Template Builder for invoice templates ──
  const openInvoiceTemplateBuilder = () => {
    localStorage.setItem('activeTab', 'pdf-templates');
    history.pushState({ tab: 'pdf-templates' }, '', window.location.pathname);
    window.dispatchEvent(new PopStateEvent('popstate', { state: { tab: 'pdf-templates' } }));
  };

  const fmt = (n: number | null | undefined) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);
  const fmtDate = (d: string | null) => d ? new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const header = (
    <PageHeader
      eyebrow="Admin · Billing"
      title="PMPM Billing"
      description="Per-Member-Per-Month billing — rate cards, member assignment, billing & invoicing"
    />
  );

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>
          <PageState variant="loading" title="Loading PMPM billing" />
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {header}
      <PageContainer>
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="py-3 text-center">
          <div className="font-display text-2xl font-semibold text-blue-600">{rateCards.length}</div>
          <div className="text-xs text-ink-500">Rate Cards</div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <div className="font-display text-2xl font-semibold text-green-600">{eligibility.length}</div>
          <div className="text-xs text-ink-500">Eligible Members</div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <div className="font-display text-2xl font-semibold text-purple-600">{billingPeriods.length}</div>
          <div className="text-xs text-ink-500">Billing Periods</div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <div className="font-display text-2xl font-semibold text-amber-600">{fmt(billingPeriods.reduce((s, bp) => s + (bp.total_amount || 0), 0))}</div>
          <div className="text-xs text-ink-500">Total Billed</div>
        </CardContent></Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { id: 'rate-cards' as const, label: 'Rate Cards', count: rateCards.length },
          { id: 'eligibility' as const, label: 'Member Eligibility', count: eligibility.length },
          { id: 'billing' as const, label: 'Billing & Invoices', count: billingPeriods.length },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-ink-500 hover:text-ink-700'}`}>
            {tab.label} <Badge variant="secondary" className="ml-1 text-xs">{tab.count}</Badge>
          </button>
        ))}
      </div>

      {/* ════════════════ RATE CARDS TAB ════════════════ */}
      {activeTab === 'rate-cards' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowRateForm(!showRateForm)}><Plus className="w-4 h-4 mr-1" /> New Rate Card</Button>
          </div>

          {showRateForm && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="py-4 space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Rate Name *</Label><Input value={rcName} onChange={e => setRcName(e.target.value)} placeholder="ECM CalAIM Rate" className="h-8" /></div>
                  <div><Label className="text-xs">Rate/Member/Month *</Label><Input value={rcRate} onChange={e => setRcRate(e.target.value)} type="number" step="0.01" placeholder="350.00" className="h-8" /></div>
                  <div><Label className="text-xs">Effective Date *</Label><Input value={rcEffDate} onChange={e => setRcEffDate(e.target.value)} type="date" className="h-8" /></div>
                  <div>
                    <Label className="text-xs">Program</Label>
                    <Select value={rcProgramId} onValueChange={setRcProgramId}>
                      <SelectTrigger className="h-8"><SelectValue placeholder="All programs" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">All programs</SelectItem>
                        {programs.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">MCO ID (optional)</Label><Input value={rcMco} onChange={e => setRcMco(e.target.value)} type="number" placeholder="MCO ID" className="h-8" /></div>
                  <div><Label className="text-xs">Risk Adj. Factor</Label><Input value={rcRaf} onChange={e => setRcRaf(e.target.value)} type="number" step="0.001" className="h-8" /></div>
                </div>
                <div><Label className="text-xs">Notes</Label><Input value={rcNotes} onChange={e => setRcNotes(e.target.value)} placeholder="Optional notes..." className="h-8" /></div>
                <div className="flex gap-2">
                  <Button onClick={createRateCard} disabled={!rcName || !rcRate || !rcEffDate || saving} size="sm">{saving ? 'Saving...' : 'Create Rate Card'}</Button>
                  <Button variant="outline" size="sm" onClick={() => setShowRateForm(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {rateCards.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-ink-400">No rate cards configured. Create one to get started.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {rateCards.map(rc => (
                <Card key={rc.id}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{rc.rate_name}</div>
                      <div className="text-xs text-ink-500 flex flex-wrap gap-x-3">
                        <span>{fmt(rc.rate_per_member_month)}/member/month</span>
                        <span>RAF: {rc.risk_adjustment_factor}</span>
                        {rc.program_name && <span className="text-blue-600">Program: {rc.program_name}</span>}
                        {rc.mco_id && <span>MCO #{rc.mco_id}</span>}
                        <span>Effective: {fmtDate(rc.effective_date)}</span>
                      </div>
                    </div>
                    <Badge variant={rc.is_active ? 'default' : 'secondary'}>{rc.is_active ? 'Active' : 'Inactive'}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════ ELIGIBILITY TAB ════════════════ */}
      {activeTab === 'eligibility' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setShowEligForm(!showEligForm); setSelectedMembers([]); setMemberQuery(''); setMemberResults([]); }}>
              <UserPlus className="w-4 h-4 mr-1" /> Assign Members
            </Button>
          </div>

          {showEligForm && (
            <Card className="border-green-200 bg-green-50/30">
              <CardContent className="py-4 space-y-4">
                <h3 className="font-semibold text-sm">Assign Members to PMPM Eligibility</h3>

                {/* Configuration row */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Eligible Start Date *</Label>
                    <Input value={eligStart} onChange={e => setEligStart(e.target.value)} type="date" className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs">Rate Card</Label>
                    <Select value={eligRateCardId} onValueChange={setEligRateCardId}>
                      <SelectTrigger className="h-8"><SelectValue placeholder="Any rate card" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Any rate card</SelectItem>
                        {rateCards.map(rc => <SelectItem key={rc.id} value={String(rc.id)}>{rc.rate_name} ({fmt(rc.rate_per_member_month)})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Program</Label>
                    <Select value={eligProgramId} onValueChange={setEligProgramId}>
                      <SelectTrigger className="h-8"><SelectValue placeholder="All programs" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">All programs</SelectItem>
                        {programs.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Member search */}
                <div>
                  <Label className="text-xs">Search Members by Name</Label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2 w-4 h-4 text-ink-400" />
                    <Input
                      value={memberQuery}
                      onChange={e => handleMemberQueryChange(e.target.value)}
                      placeholder="Type member name to search..."
                      className="h-8 pl-8"
                    />
                    {searchingMembers && <Loader2 className="absolute right-2.5 top-2 w-4 h-4 animate-spin text-ink-400" />}
                  </div>
                </div>

                {/* Search results */}
                {memberResults.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    <div className="flex items-center justify-between px-3 py-1.5 bg-paper border-b text-xs text-ink-500">
                      <span>{memberResults.length} results</span>
                      <button onClick={selectAllVisible} className="text-blue-600 hover:underline">Select all eligible</button>
                    </div>
                    {memberResults.map(m => {
                      const isSelected = selectedMembers.some(s => s.id === m.id);
                      const name = `${m.first_name || ''} ${m.last_name || ''}`.trim() || `Member #${m.id}`;
                      return (
                        <button
                          key={m.id}
                          onClick={() => !m.already_eligible && toggleMemberSelection(m)}
                          disabled={m.already_eligible}
                          className={`w-full flex items-center justify-between px-3 py-2 text-sm border-b last:border-b-0 transition-colors
                            ${m.already_eligible ? 'opacity-50 cursor-not-allowed bg-paper' : isSelected ? 'bg-blue-50' : 'hover:bg-paper'}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 border rounded flex items-center justify-center text-xs
                              ${isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-v2border'}`}>
                              {isSelected && <Check className="w-3 h-3" />}
                            </div>
                            <span className="font-medium">{name}</span>
                            {m.member_number && <span className="text-ink-400">#{m.member_number}</span>}
                          </div>
                          {m.already_eligible && <Badge variant="secondary" className="text-xs">Already eligible</Badge>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Selected members summary */}
                {selectedMembers.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-xs text-blue-700 font-medium mr-1 self-center">{selectedMembers.length} selected:</span>
                    {selectedMembers.map(m => (
                      <span key={m.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-surface border border-blue-200 rounded-full text-xs">
                        {m.first_name} {m.last_name}
                        <button onClick={() => toggleMemberSelection(m)} className="text-ink-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2">
                  <Button onClick={assignMembers} disabled={selectedMembers.length === 0 || !eligStart || saving} size="sm">
                    {saving ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Assigning...</> :
                      <><UserPlus className="w-3.5 h-3.5 mr-1" /> Assign {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}</>}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setShowEligForm(false); setSelectedMembers([]); }}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Eligible members list */}
          {eligibility.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-ink-400">No PMPM-eligible members. Use "Assign Members" to add them.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {eligibility.map(e => (
                <Card key={e.id} className={e.status === 'active' ? 'border-green-200' : 'opacity-60'}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{e.member_name || `Member #${e.member_id}`}</div>
                      <div className="text-xs text-ink-500 flex flex-wrap gap-x-3">
                        <span>Since {fmtDate(e.eligible_start)}{e.eligible_end ? ` to ${fmtDate(e.eligible_end)}` : ''}</span>
                        {e.program_name && <span className="text-blue-600">{e.program_name}</span>}
                        {e.rate_card_name && <span className="text-purple-600">{e.rate_card_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={e.status === 'active' ? 'default' : 'secondary'}>{e.status}</Badge>
                      {e.status === 'active' && (
                        <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => removeEligibility(e.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════ BILLING & INVOICES TAB ════════════════ */}
      {activeTab === 'billing' && (
        <div className="space-y-4">
          {/* Invoice template link */}
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={openInvoiceTemplateBuilder}>
              <FileText className="w-4 h-4 mr-1" /> Create Invoice Template (PDF Builder)
            </Button>
          </div>

          {/* Generate billing */}
          <Card className="border-purple-200 bg-purple-50/30">
            <CardContent className="py-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Calculator className="w-4 h-4" /> Generate Monthly Billing</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Month</Label>
                  <Select value={genMonth} onValueChange={setGenMonth}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{new Date(2026, i).toLocaleString('default', { month: 'long' })}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Year</Label><Input value={genYear} onChange={e => setGenYear(e.target.value)} type="number" className="h-8" /></div>
                <div>
                  <Label className="text-xs">Rate Card *</Label>
                  <Select value={genRateCardId} onValueChange={setGenRateCardId}>
                    <SelectTrigger className="h-8"><SelectValue placeholder="Select rate card..." /></SelectTrigger>
                    <SelectContent>
                      {rateCards.map(rc => (
                        <SelectItem key={rc.id} value={String(rc.id)}>
                          {rc.rate_name} ({fmt(rc.rate_per_member_month)})
                          {rc.program_name ? ` — ${rc.program_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={generateBilling} disabled={!genRateCardId || generating}>
                    {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Generating...</> : 'Generate Billing'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Billing periods */}
          {billingPeriods.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-ink-400">No billing periods generated yet.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {billingPeriods.map(bp => {
                const monthName = new Date(2026, bp.period_month - 1).toLocaleString('default', { month: 'long' });
                const statusColor: Record<string, string> = {
                  generated: 'bg-yellow-100 text-yellow-800', approved: 'bg-blue-100 text-blue-800',
                  invoiced: 'bg-green-100 text-green-800', paid: 'bg-emerald-100 text-emerald-800',
                };
                return (
                  <Card key={bp.id}>
                    <CardContent className="py-3 flex items-center justify-between">
                      <div>
                        <div className="font-medium">{monthName} {bp.period_year}</div>
                        <div className="text-xs text-ink-500 flex flex-wrap gap-x-3">
                          <span>{bp.eligible_member_count} members × {fmt(bp.rate_per_member)} = <strong>{fmt(bp.total_amount)}</strong></span>
                          {bp.program_name && <span className="text-blue-600">{bp.program_name}</span>}
                          {bp.rate_card_name && <span className="text-purple-600">{bp.rate_card_name}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[bp.status] || 'bg-paper-2 text-ink-500'}`}>
                          {bp.status}
                        </span>
                        {bp.status === 'generated' && (
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => approvePeriod(bp.id)}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => downloadInvoicePdf(bp.id)} disabled={downloadingPdf === bp.id}>
                          {downloadingPdf === bp.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Invoice PDF</>}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deletePeriod(bp.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
      </PageContainer>
    </div>
  );
}
